import { useContext } from 'react'
import { defaultProps, UploadContext } from './UploadContext'
import Lead from '../../elements/Lead'
import P from '../../elements/P'
import Dl from '../../elements/Dl'
import Dt from '../../elements/Dt'
import Dd from '../../elements/Dd'
import { formatNumber } from '../number-format/NumberUtils'
import { isArrayOfObjects, isArrayOfStrings } from './UploadVerify'
import Table from '../Table'
import Tr from '../table/TableTr'
import Th from '../table/TableTh'
import Td from '../table/TableTd'
import type { UploadAcceptedFileTypeObject, UploadProps } from './types'
import Flex from '../../components/Flex'

const prettifyAcceptedFileFormats = (acceptedFileTypes) =>
  acceptedFileTypes.sort().join(', ').toUpperCase()

const UploadInfo = () => {
  const context = useContext(UploadContext)

  const {
    title,
    text,
    acceptedFileTypes,
    fileTypeDescription,
    fileSizeDescription,
    fileAmountDescription,
    fileSizeContent,
    filesAmountLimit,
    fileMaxSize,
    children,
  } = context

  const prettifiedAcceptedFileFormats =
    prettifyAcceptedFileFormats(acceptedFileTypes)

  const isAcceptedFileTypeListOfStrings =
    isArrayOfStrings(acceptedFileTypes)

  const displayAcceptedFileFormatsListItem =
    isAcceptedFileTypeListOfStrings && prettifiedAcceptedFileFormats

  const displayFileMaxSizeItem =
    isAcceptedFileTypeListOfStrings && fileMaxSize

  const displayFilesAmountLimitItem =
    filesAmountLimit < defaultProps.filesAmountLimit

  const displayAcceptedFileFormatsTable =
    !displayAcceptedFileFormatsListItem &&
    isArrayOfObjects(acceptedFileTypes)

  const displayDl =
    displayAcceptedFileFormatsListItem ||
    displayFileMaxSizeItem ||
    displayFilesAmountLimitItem

  return (
    <Flex.Stack gap="small">
      {(title || text) && (
        <Flex.Stack gap="xx-small">
          {title && <Lead space="0">{title}</Lead>}

          {text && <P className="dnb-upload__text">{text}</P>}
        </Flex.Stack>
      )}
      {children}

      {displayDl && (
        <Dl bottom={0} layout="horizontal">
          {displayAcceptedFileFormatsListItem && (
            <Dl.Item>
              <Dt>{fileTypeDescription}</Dt>
              <Dd>{prettifiedAcceptedFileFormats}</Dd>
            </Dl.Item>
          )}

          {displayFileMaxSizeItem && (
            <Dl.Item>
              <Dt>{fileSizeDescription}</Dt>
              <Dd>
                {String(fileSizeContent).replace(
                  '%size',
                  formatNumber(fileMaxSize).toString()
                )}
              </Dd>
            </Dl.Item>
          )}

          {displayFilesAmountLimitItem && (
            <Dl.Item>
              <Dt>{fileAmountDescription}</Dt>
              <Dd>{filesAmountLimit}</Dd>
            </Dl.Item>
          )}
        </Dl>
      )}

      {displayAcceptedFileFormatsTable && (
        <UploadInfoAcceptedFileTypesTable />
      )}
    </Flex.Stack>
  )
}

function UploadInfoAcceptedFileTypesTable() {
  const context = useContext(UploadContext)

  const {
    acceptedFileTypes,
    fileTypeTableCaption,
    fileTypeDescription,
    fileSizeDescription,
    fileSizeContent,
    fileMaxSize,
  } = context

  const groupByFileMaxSize = (
    acceptedFileTypes: UploadProps['acceptedFileTypes'],
    fallBackFileMaxSize: UploadProps['fileMaxSize']
  ) => {
    const fileMaxSizeIsFalseOrZero = (
      fileMaxSize: UploadProps['fileMaxSize']
    ) => {
      return fileMaxSize === false || fileMaxSize === 0
    }

    const group = {}
    acceptedFileTypes.forEach((item) => {
      const itemFileMaxSize = item.fileMaxSize
      const groupName = itemFileMaxSize
        ? itemFileMaxSize
        : fileMaxSizeIsFalseOrZero(itemFileMaxSize)
          ? 0
          : fileMaxSizeIsFalseOrZero(fallBackFileMaxSize)
            ? 0
            : fallBackFileMaxSize

      group[groupName] = group[groupName] || []
      group[groupName].push(item)
    })

    return group
  }

  const acceptedFileTypesGroupedByFileMaxSize = groupByFileMaxSize(
    acceptedFileTypes,
    fileMaxSize
  )

  return (
    <Table
      border
      className="dnb-upload__accepted-file-types-table"
      size="small"
    >
      <caption className="dnb-sr-only">{fileTypeTableCaption}</caption>
      <thead>
        <Tr variant="odd">
          <Th>{fileTypeDescription}</Th>
          <Th>{fileSizeDescription}</Th>
        </Tr>
      </thead>
      <tbody>
        {Object.keys(acceptedFileTypesGroupedByFileMaxSize)
          .sort((a, b) => Number(b) - Number(a))
          .map((key) => {
            return (
              <Tr variant="odd" key={key}>
                <Td>
                  {prettifyAcceptedFileFormats(
                    acceptedFileTypesGroupedByFileMaxSize[key].map(
                      (
                        acceptedFileTypesObj: UploadAcceptedFileTypeObject
                      ) => acceptedFileTypesObj.fileType
                    )
                  )}
                </Td>
                <Td>
                  {key !== '0' &&
                    String(fileSizeContent).replace(
                      '%size',
                      formatNumber(key).toString()
                    )}
                </Td>
              </Tr>
            )
          })}
      </tbody>
    </Table>
  )
}

export default UploadInfo
